"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { ArrowUpRight, Info, Sparkles, X } from "lucide-react";
import { updateAssistantFlarePreference } from "@/lib/actions/assistant-flare-actions";
import type { AssistantExperience } from "@/lib/ai/assistant-v2/entitlement";
import { articlePath, ARTICLE_SLUGS } from "@/lib/articles/slugs";
import { ArticleLink } from "@/components/articles/article-link";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ToggleSwitch } from "@/components/ui/toggle-switch";
const ASSISTANT_FLARE_INTRO_KEY = "iloris:assistant-flare-intro-seen";
function hasSeenAssistantFlareIntro(): boolean {
  try {
    return window.localStorage?.getItem?.(ASSISTANT_FLARE_INTRO_KEY) === "1";
  } catch {
    return false;
  }
}
function rememberAssistantFlareIntro(): void {
  try {
    window.localStorage?.setItem?.(ASSISTANT_FLARE_INTRO_KEY, "1");
  } catch {
    // Storage can be unavailable in hardened browsers. Dismiss for this mount.
  }
}
interface AssistantFlareControlProps {
  experience: AssistantExperience;
  isAvailable: boolean;
  isDisabled?: boolean;
  onExperienceChange: (experience: AssistantExperience) => void;
}
export function AssistantFlareControl(t0) {
  const $ = _c(48);
  const {
    experience,
    isAvailable,
    isDisabled: t1,
    onExperienceChange
  } = t0;
  const isDisabled = t1 === undefined ? false : t1;
  const router = useRouter();
  const [isIntroOpen, setIsIntroOpen] = useState(false);
  const [error, setError] = useState(null);
  const [isPending, startTransition] = useTransition();
  const isFlareEnabled = experience === "v2";
  let t2;
  let t3;
  if ($[0] !== isAvailable) {
    t2 = () => {
      if (!isAvailable) {
        return;
      }
      if (hasSeenAssistantFlareIntro()) {
        return;
      }
      const timer = window.setTimeout(() => setIsIntroOpen(true), 0);
      return () => window.clearTimeout(timer);
    };
    t3 = [isAvailable];
    $[0] = isAvailable;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = function dismissIntro() {
      rememberAssistantFlareIntro();
      setIsIntroOpen(false);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const dismissIntro = t4;
  let t5;
  if ($[4] !== isFlareEnabled || $[5] !== onExperienceChange || $[6] !== router) {
    t5 = function toggleFlare() {
      const nextEnabled = !isFlareEnabled;
      dismissIntro();
      setError(null);
      startTransition(async () => {
        try {
          const result = await updateAssistantFlarePreference(nextEnabled);
          if ("error" in result) {
            setError(result.error);
            return;
          }
          onExperienceChange(result.experience);
          router.refresh();
        } catch {
          setError("Assistant Flare could not be updated. Please try again.");
        }
      });
    };
    $[4] = isFlareEnabled;
    $[5] = onExperienceChange;
    $[6] = router;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const toggleFlare = t5;
  let t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Sparkles className="size-3.5 shrink-0 text-primary" aria-hidden={true} />;
    t7 = <span className="truncate text-xs font-semibold">Assistant Flare</span>;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  let t8;
  if ($[10] !== isIntroOpen) {
    t8 = open => {
      if (!open && isIntroOpen) {
        dismissIntro();
      } else {
        setIsIntroOpen(open);
      }
    };
    $[10] = isIntroOpen;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <PopoverTrigger asChild={true}><button type="button" className="inline-flex size-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" aria-label="About Assistant Flare"><Info className="size-3.5" aria-hidden={true} /></button></PopoverTrigger>;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Button type="button" variant="ghost" size="icon" onClick={dismissIntro} className="absolute right-1.5 top-1.5 size-7" aria-label="Dismiss Assistant Flare introduction"><X className="size-4" /></Button>;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="pr-7"><p className="font-heading text-base font-bold">Meet Assistant Flare</p><p className="mt-1 text-sm text-muted-foreground">Compared with your current assistant, Flare:</p></div>;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <li className="rounded-md bg-background/60 px-3 py-2"><strong className="block text-sm">Searches deeper</strong><span className="text-xs text-muted-foreground">Exact words + meaning across saved work</span></li>;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <li className="rounded-md bg-background/60 px-3 py-2"><strong className="block text-sm">Checks canon</strong><span className="text-xs text-muted-foreground">Claims verified against chapters, Wiki, and plot threads</span></li>;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ul className="mt-3 space-y-2">{t12}{t13}<li className="rounded-md bg-background/60 px-3 py-2"><strong className="block text-sm">Reviews first</strong><span className="text-xs text-muted-foreground">Wiki and plot suggestions previewed, never auto-applied</span></li></ul>;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== isDisabled || $[19] !== isFlareEnabled || $[20] !== isPending || $[21] !== toggleFlare) {
    t15 = !isFlareEnabled && <Button type="button" size="sm" onClick={toggleFlare} disabled={isDisabled || isPending} className="mt-3 w-full"><Sparkles className="size-3.5" aria-hidden={true} />Try Assistant Flare</Button>;
    $[18] = isDisabled;
    $[19] = isFlareEnabled;
    $[20] = isPending;
    $[21] = toggleFlare;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = articlePath(ARTICLE_SLUGS.MEET_ASSISTANT_FLARE);
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <ArticleLink href={t16} onClick={dismissIntro} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary underline decoration-primary/40 underline-offset-4 hover:decoration-primary">See everything Flare can do<ArrowUpRight className="size-3.5" aria-hidden={true} /></ArticleLink>;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== t15) {
    t18 = <PopoverContent align="start" side="bottom" className="w-80 max-w-[calc(100vw-1.5rem)] p-0"><div className="relative overflow-hidden rounded-md bg-gradient-to-br from-primary/15 via-background to-amber-500/10 p-4">{t10}{t11}{t14}{t15}{t17}</div></PopoverContent>;
    $[25] = t15;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== isIntroOpen || $[28] !== t18 || $[29] !== t8) {
    t19 = <Popover open={isIntroOpen} onOpenChange={t8}>{t9}{t18}</Popover>;
    $[27] = isIntroOpen;
    $[28] = t18;
    $[29] = t8;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== isAvailable) {
    t20 = !isAvailable && <span className="text-[10px] font-medium text-muted-foreground">Unavailable</span>;
    $[31] = isAvailable;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== t19 || $[34] !== t20) {
    t21 = <div className="flex min-w-0 items-center gap-1.5">{t6}{t7}{t19}{t20}</div>;
    $[33] = t19;
    $[34] = t20;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  const t22 = !isAvailable || isDisabled || isPending;
  let t23;
  if ($[36] !== isFlareEnabled || $[37] !== t22 || $[38] !== toggleFlare) {
    t23 = <ToggleSwitch checked={isFlareEnabled} onChange={toggleFlare} disabled={t22} aria-label="Use Assistant Flare" />;
    $[36] = isFlareEnabled;
    $[37] = t22;
    $[38] = toggleFlare;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== t21 || $[41] !== t23) {
    t24 = <div className="flex items-center justify-between gap-3">{t21}{t23}</div>;
    $[40] = t21;
    $[41] = t23;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] !== error) {
    t25 = error && <p className="mt-1.5 text-xs text-destructive" role="alert">{error}</p>;
    $[43] = error;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  let t26;
  if ($[45] !== t24 || $[46] !== t25) {
    t26 = <div className="border-b border-border px-3 py-2" data-testid="assistant-experience-label">{t24}{t25}</div>;
    $[45] = t24;
    $[46] = t25;
    $[47] = t26;
  } else {
    t26 = $[47];
  }
  return t26;
}